import {
  ApiResponse,
  LoginCredentials,
  RegisterData,
  User,
  Subscription,
  Chatbot,
  ChatMetrics,
  Invoice,
  PricingPlan,
  SubscriptionManagement,
  SubscriptionStats,
  PaymentMethod
} from '../types';
import { mockApiService } from './mockApi';
import axios from 'axios';

// Check if we should use mock API (for development/demo)
// Set to false to use real backend API at http://localhost:8080/api
const USE_MOCK_API = process.env.REACT_APP_USE_MOCK_API === 'true';

// Export the appropriate API service
export const apiService = USE_MOCK_API ? mockApiService : createRealApiService();

// Custom error class for API errors
class ApiError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

// Helper to create error with message
const createApiError = (message: string) => new ApiError(message);

// Helper to extract error message from API response
const extractErrorMessage = (error: any, defaultMessage: string): string => {
  // Handle network errors (backend not available)
  if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
    return 'Cannot connect to server. Please ensure the backend API is running.';
  }
  
  // Handle API error responses
  if (error.response?.data) {
    // Check for 'error' field first, then 'message'
    return error.response.data.error || error.response.data.message || defaultMessage;
  }
  
  // Handle thrown errors with message
  return error.message || defaultMessage;
};

// Real API service (when backend is available)
function createRealApiService() {
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

  class ApiService {
    private api: any;

    constructor() {
      this.api = axios.create({
        baseURL: API_BASE_URL,
        timeout: 10000,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // Request interceptor to add auth token
      this.api.interceptors.request.use(
        (config: any) => {
          const token = localStorage.getItem('token');
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
          return config;
        },
        (error: any) => {
          return Promise.reject(error);
        }
      );

      // Response interceptor to handle errors
      this.api.interceptors.response.use(
        (response: any) => {
          return response;
        },
        (error: any) => {
          if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            // Only redirect if we're not on the login page already
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          }
          return Promise.reject(error);
        }
      );
    }

    // Auth endpoints
    async login(credentials: LoginCredentials): Promise<ApiResponse<{ user: User; token: string }>> {
      try {
        const response = await this.api.post('/auth/login', credentials);
        
        // Check if the response indicates failure even with HTTP 200
        if (response.data && response.data.success === false) {
          // Handle both 'message' and 'error' fields in the response
          const errorMessage = response.data.error || response.data.message || 'Invalid email or password';
          throw createApiError(errorMessage);
        }
        
        return response.data;
      } catch (error: any) {
        // Handle network errors (backend not available)
        if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
          throw createApiError('Cannot connect to server. Please ensure the backend API is running.');
        }
        // Handle API error responses
        if (error.response?.data) {
          // Check for 'error' field first, then 'message'
          const errorMessage = error.response.data.error || error.response.data.message || 'Login failed';
          throw createApiError(errorMessage);
        }
        throw createApiError(error.message || 'Login failed');
      }
    }

    async register(data: RegisterData): Promise<ApiResponse<{ user: User; token: string }>> {
      try {
        const response = await this.api.post('/auth/register', data);
        
        // Check if the response indicates failure even with HTTP 200
        if (response.data && response.data.success === false) {
          // Handle both 'error' and 'message' fields in the response
          const errorMessage = response.data.error || response.data.message || 'Registration failed';
          throw createApiError(errorMessage);
        }
        
        return response.data;
      } catch (error: any) {
        if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
          throw createApiError('Cannot connect to server. Please ensure the backend API is running.');
        }
        // Handle API error responses
        if (error.response?.data) {
          // Check for 'error' field first, then 'message'
          const errorMessage = error.response.data.error || error.response.data.message || 'Registration failed';
          throw createApiError(errorMessage);
        }
        throw createApiError(error.message || 'Registration failed');
      }
    }

    async forgotPassword(email: string): Promise<ApiResponse<any>> {
      try {
        const response = await this.api.post('/auth/forgot-password', { email });
        
        // Check if the response indicates failure even with HTTP 200
        if (response.data && response.data.success === false) {
          // Handle both 'error' and 'message' fields in the response
          const errorMessage = response.data.error || response.data.message || 'Failed to send reset email';
          throw createApiError(errorMessage);
        }
        
        return response.data;
      } catch (error: any) {
        if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
          throw createApiError('Cannot connect to server. Please ensure the backend API is running.');
        }
        // Handle API error responses
        if (error.response?.data) {
          // Check for 'error' field first, then 'message'
          const errorMessage = error.response.data.error || error.response.data.message || 'Failed to send reset email';
          throw createApiError(errorMessage);
        }
        throw createApiError(error.message || 'Failed to send reset email');
      }
    }

    async resetPassword(token: string, password: string): Promise<ApiResponse<any>> {
      try {
        const response = await this.api.post('/auth/reset-password', { token, password });
        
        // Check if the response indicates failure even with HTTP 200
        if (response.data && response.data.success === false) {
          // Handle both 'error' and 'message' fields in the response
          const errorMessage = response.data.error || response.data.message || 'Failed to reset password';
          throw createApiError(errorMessage);
        }
        
        return response.data;
      } catch (error: any) {
        if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
          throw createApiError('Cannot connect to server. Please ensure the backend API is running.');
        }
        // Handle API error responses
        if (error.response?.data) {
          // Check for 'error' field first, then 'message'
          const errorMessage = error.response.data.error || error.response.data.message || 'Failed to reset password';
          throw createApiError(errorMessage);
        }
        throw createApiError(error.message || 'Failed to reset password');
      }
    }

    // User endpoints
    async getCurrentUser(): Promise<ApiResponse<User>> {
      try {
        const response = await this.api.get('/user/me');
        
        // Check if the response indicates failure even with HTTP 200
        if (response.data && response.data.success === false) {
          // Handle both 'error' and 'message' fields in the response
          const errorMessage = response.data.error || response.data.message || 'Failed to get user data';
          throw createApiError(errorMessage);
        }
        
        return response.data;
      } catch (error: any) {
        if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
          throw createApiError('Cannot connect to server. Please ensure the backend API is running.');
        }
        // Handle API error responses
        if (error.response?.data) {
          // Check for 'error' field first, then 'message'
          const errorMessage = error.response.data.error || error.response.data.message || 'Failed to get user data';
          throw createApiError(errorMessage);
        }
        throw createApiError(error.message || 'Failed to get user data');
      }
    }

    async updateProfile(data: Partial<User>): Promise<ApiResponse<User>> {
      try {
        const response = await this.api.put('/user/profile', data);
        
        // Check if the response indicates failure even with HTTP 200
        if (response.data && response.data.success === false) {
          // Handle both 'error' and 'message' fields in the response
          const errorMessage = response.data.error || response.data.message || 'Failed to update profile';
          throw createApiError(errorMessage);
        }
        
        return response.data;
      } catch (error: any) {
        if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
          throw createApiError('Cannot connect to server. Please ensure the backend API is running.');
        }
        // Handle API error responses
        if (error.response?.data) {
          // Check for 'error' field first, then 'message'
          const errorMessage = error.response.data.error || error.response.data.message || 'Failed to update profile';
          throw createApiError(errorMessage);
        }
        throw createApiError(error.message || 'Failed to update profile');
      }
    }

    // Payment method endpoints
    async getPaymentMethod(): Promise<ApiResponse<PaymentMethod>> {
      try {
        const response = await this.api.get('/user/payment-method');
        
        // Check if the response indicates failure even with HTTP 200
        if (response.data && response.data.success === false) {
          // Handle both 'error' and 'message' fields in the response
          const errorMessage = response.data.error || response.data.message || 'Failed to get payment method';
          throw createApiError(errorMessage);
        }
        
        return response.data;
      } catch (error: any) {
        throw createApiError(extractErrorMessage(error, 'Failed to get payment method'));
      }
    }

    // Subscription endpoints
    async getSubscription(): Promise<ApiResponse<Subscription>> {
      try {
        const response = await this.api.get('/subscription');
        
        // Check if the response indicates failure even with HTTP 200
        if (response.data && response.data.success === false) {
          // Handle both 'error' and 'message' fields in the response
          const errorMessage = response.data.error || response.data.message || 'Failed to get subscription';
          throw createApiError(errorMessage);
        }
        
        return response.data;
      } catch (error: any) {
        throw createApiError(extractErrorMessage(error, 'Failed to get subscription'));
      }
    }

    async createSubscription(planId: string, paymentMethodId: string): Promise<ApiResponse<Subscription>> {
      try {
        const response = await this.api.post('/subscription', { planId, paymentMethodId });
        
        // Check if the response indicates failure even with HTTP 200
        if (response.data && response.data.success === false) {
          // Handle both 'error' and 'message' fields in the response
          const errorMessage = response.data.error || response.data.message || 'Failed to create subscription';
          throw createApiError(errorMessage);
        }
        
        return response.data;
      } catch (error: any) {
        throw createApiError(extractErrorMessage(error, 'Failed to create subscription'));
      }
    }

    async updateSubscription(planId: string): Promise<ApiResponse<Subscription>> {
      try {
        const response = await this.api.put('/subscription', { planId });
        
        // Check if the response indicates failure even with HTTP 200
        if (response.data && response.data.success === false) {
          // Handle both 'error' and 'message' fields in the response
          const errorMessage = response.data.error || response.data.message || 'Failed to update subscription';
          throw createApiError(errorMessage);
        }
        
        return response.data;
      } catch (error: any) {
        throw createApiError(extractErrorMessage(error, 'Failed to update subscription'));
      }
    }

    async cancelSubscription(): Promise<ApiResponse<any>> {
      try {
        const response = await this.api.delete('/subscription');
        
        // Check if the response indicates failure even with HTTP 200
        if (response.data && response.data.success === false) {
          // Handle both 'error' and 'message' fields in the response
          const errorMessage = response.data.error || response.data.message || 'Failed to cancel subscription';
          throw createApiError(errorMessage);
        }
        
        return response.data;
      } catch (error: any) {
        throw createApiError(extractErrorMessage(error, 'Failed to cancel subscription'));
      }
    }

    async getSubscriptionPricingPlans(): Promise<ApiResponse<PricingPlan[]>> {
      try {
        const response = await this.api.get('/subscription/pricing-plans');
        
        // Check if the response indicates failure even with HTTP 200
        if (response.data && response.data.success === false) {
          // Handle both 'error' and 'message' fields in the response
          const errorMessage = response.data.error || response.data.message || 'Failed to get pricing plans';
          throw createApiError(errorMessage);
        }
        
        return response.data;
      } catch (error: any) {
        throw createApiError(extractErrorMessage(error, 'Failed to get pricing plans'));
      }
    }

    // Chatbot endpoints
    async getChatbot(): Promise<ApiResponse<Chatbot>> {
      try {
        const response = await this.api.get('/chatbot');
        
        // Check if the response indicates failure even with HTTP 200
        if (response.data && response.data.success === false) {
          // Handle both 'error' and 'message' fields in the response
          const errorMessage = response.data.error || response.data.message || 'Failed to get chatbot';
          throw createApiError(errorMessage);
        }
        
        return response.data;
      } catch (error: any) {
        throw createApiError(extractErrorMessage(error, 'Failed to get chatbot'));
      }
    }

    async updateChatbot(data: Partial<Chatbot>): Promise<ApiResponse<Chatbot>> {
      try {
        const response = await this.api.put('/chatbot', data);
        
        // Check if the response indicates failure even with HTTP 200
        if (response.data && response.data.success === false) {
          // Handle both 'error' and 'message' fields in the response
          const errorMessage = response.data.error || response.data.message || 'Failed to update chatbot';
          throw createApiError(errorMessage);
        }
        
        return response.data;
      } catch (error: any) {
        throw createApiError(extractErrorMessage(error, 'Failed to update chatbot'));
      }
    }

    // Dashboard endpoints
    async getMetrics(): Promise<ApiResponse<ChatMetrics>> {
      try {
        const response = await this.api.get('/dashboard/metrics');
        
        // Check if the response indicates failure even with HTTP 200
        if (response.data && response.data.success === false) {
          // Handle both 'error' and 'message' fields in the response
          const errorMessage = response.data.error || response.data.message || 'Failed to get metrics';
          throw createApiError(errorMessage);
        }
        
        return response.data;
      } catch (error: any) {
        throw createApiError(extractErrorMessage(error, 'Failed to get metrics'));
      }
    }

    // Invoice endpoints
    async getInvoices(): Promise<ApiResponse<Invoice[]>> {
      try {
        const response = await this.api.get('/invoices');
        
        // Check if the response indicates failure even with HTTP 200
        if (response.data && response.data.success === false) {
          // Handle both 'error' and 'message' fields in the response
          const errorMessage = response.data.error || response.data.message || 'Failed to get invoices';
          throw createApiError(errorMessage);
        }
        
        return response.data;
      } catch (error: any) {
        throw createApiError(extractErrorMessage(error, 'Failed to get invoices'));
      }
    }

    async downloadInvoice(invoiceId: string): Promise<Blob> {
      try {
        const response = await this.api.get(`/invoices/${invoiceId}/download`, {
          responseType: 'blob',
        });
        return response.data;
      } catch (error: any) {
        throw createApiError(extractErrorMessage(error, 'Failed to download invoice'));
      }
    }

    // Admin endpoints
    async getUsers(): Promise<ApiResponse<User[]>> {
      try {
        const response = await this.api.get('/admin/users');
        
        // Check if the response indicates failure even with HTTP 200
        if (response.data && response.data.success === false) {
          // Handle both 'error' and 'message' fields in the response
          const errorMessage = response.data.error || response.data.message || 'Failed to get users';
          throw createApiError(errorMessage);
        }
        
        return response.data;
      } catch (error: any) {
        throw createApiError(extractErrorMessage(error, 'Failed to get users'));
      }
    }

    async updateUserStatus(userId: string, status: string): Promise<ApiResponse<User>> {
      try {
        const response = await this.api.put(`/admin/users/${userId}`, { status });
        
        // Check if the response indicates failure even with HTTP 200
        if (response.data && response.data.success === false) {
          // Handle both 'error' and 'message' fields in the response
          const errorMessage = response.data.error || response.data.message || 'Failed to update user status';
          throw createApiError(errorMessage);
        }
        
        return response.data;
      } catch (error: any) {
        throw createApiError(extractErrorMessage(error, 'Failed to update user status'));
      }
    }

    // Subscription Management endpoints
    async getSubscriptionManagements(): Promise<ApiResponse<SubscriptionManagement[]>> {
      try {
        const response = await this.api.get('/admin/subscriptions');
        
        // Check if the response indicates failure even with HTTP 200
        if (response.data && response.data.success === false) {
          // Handle both 'error' and 'message' fields in the response
          const errorMessage = response.data.error || response.data.message || 'Failed to get subscriptions';
          throw createApiError(errorMessage);
        }
        
        return response.data;
      } catch (error: any) {
        throw createApiError(extractErrorMessage(error, 'Failed to get subscriptions'));
      }
    }

    async getPricingPlans(): Promise<ApiResponse<PricingPlan[]>> {
      try {
        const response = await this.api.get('/admin/pricing-plans');
        
        // Check if the response indicates failure even with HTTP 200
        if (response.data && response.data.success === false) {
          // Handle both 'error' and 'message' fields in the response
          const errorMessage = response.data.error || response.data.message || 'Failed to get pricing plans';
          throw createApiError(errorMessage);
        }
        
        return response.data;
      } catch (error: any) {
        throw createApiError(extractErrorMessage(error, 'Failed to get pricing plans'));
      }
    }

    async createPricingPlan(plan: Omit<PricingPlan, 'id' | 'createdAt' | 'updatedAt'>): Promise<ApiResponse<PricingPlan>> {
      try {
        const response = await this.api.post('/admin/pricing-plans', plan);
        
        // Check if the response indicates failure even with HTTP 200
        if (response.data && response.data.success === false) {
          // Handle both 'error' and 'message' fields in the response
          const errorMessage = response.data.error || response.data.message || 'Failed to create pricing plan';
          throw createApiError(errorMessage);
        }
        
        return response.data;
      } catch (error: any) {
        throw createApiError(extractErrorMessage(error, 'Failed to create pricing plan'));
      }
    }

    async updatePricingPlan(id: string, plan: Partial<PricingPlan>): Promise<ApiResponse<PricingPlan>> {
      try {
        const response = await this.api.put(`/admin/pricing-plans/${id}`, plan);
        
        // Check if the response indicates failure even with HTTP 200
        if (response.data && response.data.success === false) {
          // Handle both 'error' and 'message' fields in the response
          const errorMessage = response.data.error || response.data.message || 'Failed to update pricing plan';
          throw createApiError(errorMessage);
        }
        
        return response.data;
      } catch (error: any) {
        throw createApiError(extractErrorMessage(error, 'Failed to update pricing plan'));
      }
    }

    async deletePricingPlan(id: string): Promise<ApiResponse<any>> {
      try {
        const response = await this.api.delete(`/admin/pricing-plans/${id}`);
        
        // Check if the response indicates failure even with HTTP 200
        if (response.data && response.data.success === false) {
          // Handle both 'error' and 'message' fields in the response
          const errorMessage = response.data.error || response.data.message || 'Failed to delete pricing plan';
          throw createApiError(errorMessage);
        }
        
        return response.data;
      } catch (error: any) {
        throw createApiError(extractErrorMessage(error, 'Failed to delete pricing plan'));
      }
    }

    async getSubscriptionStats(): Promise<ApiResponse<SubscriptionStats>> {
      try {
        const response = await this.api.get('/admin/subscription-stats');
        
        // Check if the response indicates failure even with HTTP 200
        if (response.data && response.data.success === false) {
          // Handle both 'error' and 'message' fields in the response
          const errorMessage = response.data.error || response.data.message || 'Failed to get subscription stats';
          throw createApiError(errorMessage);
        }
        
        return response.data;
      } catch (error: any) {
        throw createApiError(extractErrorMessage(error, 'Failed to get subscription stats'));
      }
    }

    async updateSubscriptionStatus(subscriptionId: string, status: string): Promise<ApiResponse<SubscriptionManagement>> {
      try {
        const response = await this.api.put(`/admin/subscriptions/${subscriptionId}`, { status });
        
        // Check if the response indicates failure even with HTTP 200
        if (response.data && response.data.success === false) {
          // Handle both 'error' and 'message' fields in the response
          const errorMessage = response.data.error || response.data.message || 'Failed to update subscription status';
          throw createApiError(errorMessage);
        }
        
        return response.data;
      } catch (error: any) {
        throw createApiError(extractErrorMessage(error, 'Failed to update subscription status'));
      }
    }
  }

  return new ApiService();
}

// Export types for use in components
export type ApiServiceType = typeof apiService;